import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import {
  Badge,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  Input,
  Label,
} from "@nasam/ui";
import { publishToast, queryKeys, useAuthStore, useLanguageStore } from "@nasam/kernel";

import { useCompanyOptions } from "../../../contracts";
import type {
  CompaniesRepository,
  DeviceListItem,
  DevicesRepository,
} from "../../../contracts";
import {
  useGprsCatalogQuery,
  useGprsDeviceOptions,
  useSendGprsCommandMutation,
} from "../application";
import type {
  GprsCatalogCommand,
  GprsCatalogQuery,
  GprsCommandParameter,
  GprsCommandsRepository,
  SendGprsCommandInput,
} from "../domain";
import { GprsDialogHeader } from "./GprsDialogHeader";

/** The IMEI picker's "All" entry -- not a real device id. */
const ALL_DEVICES = "__all__";
/** The command picker's "write your own" entry -- not a catalog id. */
const CUSTOM_COMMAND = "__custom__";
/** The API takes printable ASCII only, at most 500 characters. */
const CUSTOM_TEXT_PATTERN = /^[\x20-\x7E]+$/;
const CUSTOM_TEXT_MAX = 500;

/** A custom command as sent (`customText`): the command, then its optional
 *  parameters after a space -- the API has no separate field for them. */
function customCommandText(command: string, parameters: string): string {
  const text = command.trim();
  const extra = parameters.trim();
  return extra === "" ? text : `${text} ${extra}`;
}

/** Opened from somewhere that already knows the device (Tracking's card
 *  action): its company and IMEI. The device is looked up by IMEI and chosen
 *  to start with; the form stays editable. */
export interface SendGprsCommandPreset {
  /** Chosen for staff, whose devices are picked per company. */
  readonly company: { readonly id: string; readonly label: string } | null;
  readonly imei: string;
}

export interface SendGprsCommandDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly repository: GprsCommandsRepository;
  readonly companies?: CompaniesRepository | undefined;
  readonly devices?: DevicesRepository | undefined;
  readonly preset?: SendGprsCommandPreset | undefined;
}

interface PickedCompany {
  readonly id: string;
  readonly label: string;
}

/** What's wrong with a parameter's value, as an i18n key and values; `null`
 *  when it's fine. INTEGER: a whole number within the bounds; TEXT: a
 *  length within them, matching the pattern. */
function parameterProblem(
  parameter: GprsCommandParameter,
  raw: string,
): { readonly key: string; readonly values?: Record<string, unknown> } | null {
  const value = raw.trim();
  if (value === "") return parameter.required ? { key: "validation:required" } : null;
  const min = parameter.minValue;
  const max = parameter.maxValue;
  if (parameter.kind === "INTEGER") {
    const number = Number(value);
    if (!Number.isInteger(number)) return { key: "send.errors.wholeNumber" };
    if ((min !== null && number < min) || (max !== null && number > max)) {
      return { key: "send.errors.parameterRange", values: { min: min ?? "-", max: max ?? "-" } };
    }
    return null;
  }
  if ((min !== null && value.length < min) || (max !== null && value.length > max)) {
    return { key: "send.errors.parameterLength", values: { min: min ?? 0, max: max ?? "-" } };
  }
  if (parameter.pattern !== null) {
    try {
      if (!new RegExp(parameter.pattern).test(value)) return { key: "send.errors.parameterFormat" };
    } catch {
      // A pattern the browser can't read: the backend still checks it.
    }
  }
  return null;
}

/**
 * Send GPRS Command -- Company (staff only, several) -> IMEI (one, several,
 * or All, each showing its asset name) -> Command (from the catalog for the
 * chosen devices, or written by hand) -> its parameters, shown
 * automatically. Each device gets its own command, PENDING on the list
 * until it answers.
 */
export function SendGprsCommandDialog({
  open,
  onOpenChange,
  repository,
  companies,
  devices,
  preset,
}: SendGprsCommandDialogProps) {
  const { t } = useTranslation(["gprs", "validation"]);
  const isAr = useLanguageStore((state) => state.language) === "ar";
  const user = useAuthStore((state) => state.user);
  // Staff pick the companies; a client user's devices are always their own
  // account's -- same rule AssignSensorStepOne applies.
  const isStaff = user?.userType === "staff";

  const [pickedCompanies, setPickedCompanies] = useState<readonly PickedCompany[]>(() =>
    isStaff && preset?.company != null ? [preset.company] : [],
  );
  const [allDevices, setAllDevices] = useState(false);
  const [pickedDevices, setPickedDevices] = useState<readonly DeviceListItem[]>([]);

  // The preset's device, found by its IMEI among the company's active ones.
  const presetCompanyId = isStaff ? preset?.company?.id : undefined;
  const presetLookup = useQuery({
    queryKey: [...queryKeys.devices.lists(), "gprs-preset", preset?.imei, presetCompanyId],
    queryFn: () =>
      devices!.list({
        limit: 1,
        status: "ACTIVE",
        imei: preset!.imei,
        ...(presetCompanyId === undefined ? {} : { accountId: presetCompanyId }),
      }),
    enabled: open && preset !== undefined && devices !== undefined,
  });
  const presetDevice = presetLookup.data?.data.find((device) => device.imei === preset?.imei);
  const presetAppliedRef = useRef(false);
  useEffect(() => {
    if (presetAppliedRef.current || presetDevice === undefined) return;
    presetAppliedRef.current = true;
    setPickedDevices([presetDevice]);
  }, [presetDevice]);
  const presetNotFound = presetLookup.isSuccess && presetDevice === undefined;
  const [commandId, setCommandId] = useState("");
  const [customText, setCustomText] = useState("");
  const [customParameters, setCustomParameters] = useState("");
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const sendMutation = useSendGprsCommandMutation(repository);
  const isBusy = sendMutation.isPending;

  const companyOptions = useCompanyOptions(companies);
  const accountIds = isStaff ? pickedCompanies.map((company) => company.id) : undefined;
  const canPickDevices = !isStaff || pickedCompanies.length > 0;
  const deviceOptions = useGprsDeviceOptions(
    canPickDevices ? devices : undefined,
    accountIds,
  );

  // The catalog for the chosen targets: only the commands they take, each
  // with how many of them do; every command before any is chosen.
  const catalogTargets: GprsCatalogQuery = allDevices
    ? accountIds === undefined
      ? {}
      : { accountIds }
    : pickedDevices.length === 0
      ? {}
      : { deviceIds: pickedDevices.map((device) => device.id) };
  const catalogQuery = useGprsCatalogQuery(repository, catalogTargets, open);
  const catalog = catalogQuery.data ?? [];
  const isCustom = commandId === CUSTOM_COMMAND;
  const selectedCommand: GprsCatalogCommand | undefined = catalog.find(
    (entry) => entry.id === commandId,
  );
  const parameterDefinitions = selectedCommand?.parameters ?? [];

  const assetName = (device: DeviceListItem) =>
    device.asset === null
      ? null
      : isAr
        ? device.asset.nameAr || device.asset.nameEn
        : device.asset.nameEn || device.asset.nameAr;
  const commandName = (entry: GprsCatalogCommand) =>
    isAr ? entry.nameAr || entry.nameEn : entry.nameEn || entry.nameAr;
  const parameterLabel = (parameter: GprsCommandParameter) =>
    isAr ? parameter.labelAr || parameter.labelEn : parameter.labelEn || parameter.labelAr;

  const pickedCompanyIds = new Set(pickedCompanies.map((company) => company.id));
  const pickedDeviceIds = new Set(pickedDevices.map((device) => device.id));

  const deviceComboboxOptions = useMemo(
    () => [
      ...(allDevices
        ? []
        : [{ value: ALL_DEVICES, label: t("send.allDevices") }]),
      ...deviceOptions.devices
        .filter((device) => !pickedDeviceIds.has(device.id))
        .map((device) => ({
          value: device.id,
          label: device.imei,
          description: assetName(device) ?? t("send.noAsset"),
        })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allDevices, deviceOptions.devices, pickedDevices, isAr, t],
  );

  const commandOptions = [
    ...catalog.map((entry) => ({
      value: entry.id,
      label: commandName(entry),
      ...(entry.supportedDevices === null
        ? {}
        : { description: t("send.supportedBy", { count: entry.supportedDevices }) }),
    })),
    { value: CUSTOM_COMMAND, label: t("send.customCommandOption") },
  ];

  function clearErrors(matches: (key: string) => boolean) {
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !matches(key))),
    );
  }

  function clearError(key: string) {
    clearErrors((candidate) => candidate === key);
  }

  function reset() {
    setPickedCompanies([]);
    setAllDevices(false);
    setPickedDevices([]);
    setCommandId("");
    setCustomText("");
    setCustomParameters("");
    setParameters({});
    setErrors({});
    companyOptions.onSearchChange("");
    deviceOptions.onSearchChange("");
  }

  function handleOpenChange(next: boolean) {
    if (isBusy) return;
    if (!next) reset();
    onOpenChange(next);
  }

  function addCompany(id: string) {
    const option = companyOptions.options.find((o) => o.value === id);
    if (option === undefined || pickedCompanyIds.has(id)) return;
    setPickedCompanies([...pickedCompanies, { id, label: option.label }]);
  }

  function removeCompany(id: string) {
    const remaining = pickedCompanies.filter((company) => company.id !== id);
    setPickedCompanies(remaining);
    // A removed company's devices can't stay selected.
    setPickedDevices(pickedDevices.filter((device) => device.account.id !== id));
    if (remaining.length === 0) setAllDevices(false);
  }

  function addDevice(id: string) {
    clearError("devices");
    if (id === ALL_DEVICES) {
      setAllDevices(true);
      setPickedDevices([]);
      return;
    }
    const device = deviceOptions.devices.find((d) => d.id === id);
    if (device === undefined) return;
    setPickedDevices([...pickedDevices, device]);
  }

  function changeCommand(next: string) {
    setCommandId(next);
    setCustomText("");
    setCustomParameters("");
    setParameters({});
    clearErrors(
      (key) =>
        key === "command" ||
        key === "customText" ||
        key === "customParameters" ||
        key.startsWith("param."),
    );
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!allDevices && pickedDevices.length === 0) {
      next.devices = t("validation:required");
    }
    if (commandId === "") {
      next.command = t("validation:required");
    } else if (isCustom) {
      const text = customText.trim();
      const extra = customParameters.trim();
      const invalid = t("send.errors.customText", { max: CUSTOM_TEXT_MAX });
      if (text === "") next.customText = t("validation:required");
      else if (text.length > CUSTOM_TEXT_MAX || !CUSTOM_TEXT_PATTERN.test(text)) {
        next.customText = invalid;
      }
      // Optional; with the command it must still fit one custom text.
      if (extra !== "" && !CUSTOM_TEXT_PATTERN.test(extra)) {
        next.customParameters = invalid;
      } else if (
        next.customText === undefined &&
        customCommandText(text, extra).length > CUSTOM_TEXT_MAX
      ) {
        next.customParameters = invalid;
      }
    } else if (selectedCommand === undefined) {
      // Picked, then the devices changed and none of them takes it any more.
      next.command = t("send.errors.commandUnsupported");
    }
    for (const parameter of parameterDefinitions) {
      const problem = parameterProblem(parameter, parameters[parameter.name] ?? "");
      if (problem !== null) {
        next[`param.${parameter.name}`] = t(problem.key, problem.values ?? {});
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function toInput(): SendGprsCommandInput {
    const targets = allDevices
      ? { all: true as const, ...(accountIds === undefined ? {} : { accountIds }) }
      : {
          deviceIds: pickedDevices.map((device) => device.id),
          ...(accountIds === undefined ? {} : { accountIds }),
        };
    if (isCustom) {
      return { ...targets, customText: customCommandText(customText, customParameters) };
    }
    const params: Record<string, string | number> = {};
    for (const parameter of parameterDefinitions) {
      const value = (parameters[parameter.name] ?? "").trim();
      if (value === "") continue;
      params[parameter.name] = parameter.kind === "INTEGER" ? Number(value) : value;
    }
    return { ...targets, commandTypeId: commandId, params };
  }

  function handleSubmit() {
    if (!validate()) return;
    sendMutation.mutate(toInput(), {
      onSuccess: (result) => {
        publishToast(
          result.failed === 0
            ? { kind: "success", title: t("send.success", { count: result.total }) }
            : {
                kind: "warning",
                title: t("send.partialSuccess", {
                  sent: result.pending,
                  total: result.total,
                  failed: result.failed,
                }),
              },
        );
        reset();
        onOpenChange(false);
      },
      // No error toast here -- the app-wide MutationCache.onError already
      // publishes the backend's message. The dialog stays open, input intact.
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        closeLabel={t("send.close")}
        showCloseButton={false}
        className="gap-4 rounded-lg border-0 p-0 pb-4 sm:max-w-lg"
      >
        <GprsDialogHeader
          title={t("send.title")}
          subtitle={t("send.subtitle")}
          closeLabel={t("send.close")}
        />

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4">
          {isStaff ? (
            <div className="space-y-2">
              <Label>{t("send.company")}</Label>
              <Combobox
                value=""
                onValueChange={addCompany}
                placeholder={t("send.companyPlaceholder")}
                searchPlaceholder={t("send.companySearch")}
                emptyMessage={t("send.companyEmpty")}
                loadingLabel={t("common:loading")}
                aria-label={t("send.company")}
                disabled={isBusy || companies === undefined}
                {...companyOptions}
                options={companyOptions.options.filter(
                  (option) => !pickedCompanyIds.has(option.value),
                )}
              />
              {pickedCompanies.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {pickedCompanies.map((company) => (
                    <Badge key={company.id} variant="purple" className="gap-1 pe-1.5">
                      {company.label}
                      <button
                        type="button"
                        aria-label={t("send.remove", { name: company.label })}
                        onClick={() => removeCompany(company.id)}
                        disabled={isBusy}
                        className="rounded-full p-0.5 hover:bg-black/10"
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>
              {t("send.imei")} <span className="text-fail-text">*</span>
            </Label>
            <Combobox
              value=""
              onValueChange={addDevice}
              placeholder={t("send.imeiPlaceholder")}
              searchPlaceholder={t("send.imeiSearch")}
              emptyMessage={t("send.imeiEmpty")}
              loadingLabel={t("common:loading")}
              aria-label={t("send.imei")}
              error={errors.devices !== undefined}
              disabled={isBusy || !canPickDevices || devices === undefined}
              searchValue={deviceOptions.searchValue}
              onSearchChange={deviceOptions.onSearchChange}
              isLoading={deviceOptions.isLoading}
              options={deviceComboboxOptions}
            />
            {!canPickDevices ? (
              <p className="text-body-s-r text-text-placeholder">
                {t("send.selectCompanyFirst")}
              </p>
            ) : null}
            {presetNotFound && pickedDevices.length === 0 ? (
              <p role="status" className="text-body-s-r text-text-placeholder">
                {t("send.presetNotFound", { imei: preset?.imei })}
              </p>
            ) : null}
            {allDevices || pickedDevices.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {allDevices ? (
                  <Badge variant="purple" className="gap-1 pe-1.5">
                    {t("send.allDevicesChip")}
                    <button
                      type="button"
                      aria-label={t("send.remove", { name: t("send.allDevicesChip") })}
                      onClick={() => setAllDevices(false)}
                      disabled={isBusy}
                      className="rounded-full p-0.5 hover:bg-black/10"
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </Badge>
                ) : (
                  pickedDevices.map((device) => (
                    <Badge key={device.id} variant="purple" className="gap-1 pe-1.5">
                      <span dir="ltr">{device.imei}</span>
                      <span className="text-text-placeholder">
                        · {assetName(device) ?? t("send.noAsset")}
                      </span>
                      <button
                        type="button"
                        aria-label={t("send.remove", { name: device.imei })}
                        onClick={() =>
                          setPickedDevices(pickedDevices.filter((d) => d.id !== device.id))
                        }
                        disabled={isBusy}
                        className="rounded-full p-0.5 hover:bg-black/10"
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            ) : null}
            {errors.devices !== undefined ? (
              <p className="text-body-s-r text-fail-text">{errors.devices}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>
              {t("send.command")} <span className="text-fail-text">*</span>
            </Label>
            <Combobox
              value={commandId}
              onValueChange={changeCommand}
              placeholder={t("send.commandPlaceholder")}
              searchPlaceholder={t("send.commandSearch")}
              emptyMessage={t("send.commandEmpty")}
              loadingLabel={t("common:loading")}
              aria-label={t("send.command")}
              error={errors.command !== undefined}
              disabled={isBusy}
              isLoading={catalogQuery.isLoading}
              options={commandOptions}
            />
            <p className="text-body-s-r text-primary">{t("send.commandHint")}</p>
            {catalogQuery.isError ? (
              <p className="text-body-s-r text-fail-text">{t("send.errors.catalogFailed")}</p>
            ) : null}
            {errors.command !== undefined ? (
              <p className="text-body-s-r text-fail-text">{errors.command}</p>
            ) : null}
            {isCustom ? (
              <>
                <Input
                  dir="ltr"
                  aria-label={t("send.customCommand")}
                  placeholder={t("send.customCommandPlaceholder")}
                  value={customText}
                  maxLength={CUSTOM_TEXT_MAX}
                  onChange={(event) => {
                    setCustomText(event.target.value);
                    clearError("customText");
                  }}
                  aria-invalid={errors.customText !== undefined}
                  disabled={isBusy}
                />
                {errors.customText !== undefined ? (
                  <p className="text-body-s-r text-fail-text">{errors.customText}</p>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="space-y-2">
            {isCustom ? (
              <>
                <Label htmlFor="gprs-custom-parameters">{t("send.customParameters")}</Label>
                <Input
                  id="gprs-custom-parameters"
                  dir="ltr"
                  placeholder={t("send.customParametersPlaceholder")}
                  value={customParameters}
                  maxLength={CUSTOM_TEXT_MAX}
                  onChange={(event) => {
                    setCustomParameters(event.target.value);
                    clearError("customParameters");
                  }}
                  aria-invalid={errors.customParameters !== undefined}
                  disabled={isBusy}
                />
                {errors.customParameters !== undefined ? (
                  <p className="text-body-s-r text-fail-text">{errors.customParameters}</p>
                ) : (
                  <p className="text-body-s-r text-text-placeholder">
                    {t("send.customParametersHint")}
                  </p>
                )}
              </>
            ) : (
              <Label>{t("send.parameters")}</Label>
            )}
            {isCustom ? null : parameterDefinitions.length === 0 ? (
              <>
                <Input
                  disabled
                  aria-label={t("send.parameters")}
                  placeholder={t("send.parametersPlaceholder")}
                />
                <p className="text-body-s-r text-text-placeholder">
                  {t("send.parametersHint")}
                </p>
              </>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {parameterDefinitions.map((parameter) => {
                  const errorKey = `param.${parameter.name}`;
                  return (
                    <div key={parameter.name} className="space-y-1">
                      <Label htmlFor={`gprs-param-${parameter.name}`}>
                        {parameterLabel(parameter)}
                        {parameter.required ? (
                          <span className="text-fail-text"> *</span>
                        ) : null}
                      </Label>
                      <Input
                        id={`gprs-param-${parameter.name}`}
                        dir="ltr"
                        type={parameter.kind === "INTEGER" ? "number" : "text"}
                        inputMode={parameter.kind === "INTEGER" ? "numeric" : undefined}
                        value={parameters[parameter.name] ?? ""}
                        onChange={(event) => {
                          setParameters({
                            ...parameters,
                            [parameter.name]: event.target.value,
                          });
                          clearError(errorKey);
                        }}
                        aria-invalid={errors[errorKey] !== undefined}
                        disabled={isBusy}
                      />
                      {errors[errorKey] !== undefined ? (
                        <p className="text-body-s-r text-fail-text">{errors[errorKey]}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-4">
          <Button
            type="button"
            variant="secondary"
            disabled={isBusy}
            onClick={() => handleOpenChange(false)}
          >
            {t("send.cancel")}
          </Button>
          <Button
            type="button"
            className="gap-1 md:w-45"
            disabled={isBusy}
            onClick={handleSubmit}
          >
            {isBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            {t("send.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
